import { buildCompanionFreshness, type CompanionFreshness } from "./freshness";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";

export type CompanionDecisionAction =
  | "go-now"
  | "go-later"
  | "go-elsewhere"
  | "do-not-bother";

export type CompanionDecisionConfidence = "high" | "medium" | "low";

export type CompanionDecision = {
  action: CompanionDecisionAction;
  score: number | null;
  placeId: string | null;
  verdict: string;
  confidence: CompanionDecisionConfidence;
  reason: string;
};

export type CompanionDecisionPlace = {
  id: string;
  name: string;
  score: number | null;
  scoreKind: "generic-fallback" | "sport-specific";
  verdict: string | null;
  bestWindow: string | null;
  updatedAt: string | null;
  safetyNotes?: string[];
  safetyState?: ConditionSafetyState;
};

export type CompanionDecisionCall = {
  id?: string;
  title: string;
  placeName: string | null;
  verdict: string | null;
  startsAt?: string | null;
  href?: string;
} | null;

export type CompanionDecisionBriefing = {
  id?: string;
  title: string;
  placeName: string | null;
  publishedAt: string | null;
  href?: string;
} | null;

export type CompanionDecisionInput = {
  savedPlaces: CompanionDecisionPlace[];
  latestCall: CompanionDecisionCall;
  latestBriefing: CompanionDecisionBriefing;
  now?: Date;
};

export const ELSEWHERE_SCORE_GAP = 1.2;
export const COMPANION_GO_NOW_SCORE = 7.5;
export const COMPANION_GO_LATER_SCORE = 5.5;

export function buildCompanionDecision(input: CompanionDecisionInput): CompanionDecision {
  const now = input.now ?? inferDecisionNow(input);
  const focusPlace = input.savedPlaces[0] ?? null;
  const rankedPlaces = rankCompanionDecisionPlaces(input.savedPlaces, now);
  const bestPlace = rankedPlaces.find((place) => place.score !== null) ?? rankedPlaces[0] ?? null;

  if (!bestPlace || bestPlace.score === null) {
    return missingScoreDecision(input);
  }

  const freshness = scoreFreshness(bestPlace.updatedAt, now);
  const confidence = decisionConfidence(bestPlace, freshness, input);
  const bestDecisionScore = decisionScore(bestPlace, now);
  const focusDecisionScore = focusPlace ? decisionScore(focusPlace, now) : null;

  if (
    bestPlace.score >= COMPANION_GO_LATER_SCORE &&
    bestPlace.safetyState === "flagged-red"
  ) {
    return {
      action: "go-later",
      score: bestPlace.score,
      placeId: bestPlace.id,
      confidence,
      verdict: `Check safety: ${bestPlace.name} has a red safety flag.`,
      reason: `${scoreReason(bestPlace, freshness)} ${supportReason(input, bestPlace)}`,
    };
  }

  if (
    focusPlace &&
    focusPlace.id !== bestPlace.id &&
    focusPlace.score !== null &&
    bestDecisionScore >= COMPANION_GO_LATER_SCORE + 1 &&
    focusDecisionScore !== null &&
    bestDecisionScore - focusDecisionScore >= ELSEWHERE_SCORE_GAP &&
    isUsableFreshness(freshness) &&
    bestPlace.safetyState !== "flagged-red"
  ) {
    return {
      action: "go-elsewhere",
      score: bestPlace.score,
      placeId: bestPlace.id,
      confidence,
      verdict: `Go elsewhere: ${bestPlace.name} looks stronger than ${focusPlace.name}.`,
      reason: `${scoreReason(bestPlace, freshness)} ${focusPlace.name} is ${formatScore(
        focusPlace.score,
      )}, so do not force that plan.`,
    };
  }

  if (
    bestPlace.score >= COMPANION_GO_NOW_SCORE &&
    isUsableFreshness(freshness)
  ) {
    return {
      action: "go-now",
      score: bestPlace.score,
      placeId: bestPlace.id,
      confidence,
      verdict: `Go now: ${bestPlace.name} is the strongest saved signal.`,
      reason: `${scoreReason(bestPlace, freshness)} ${supportReason(input, bestPlace)}`,
    };
  }

  if (bestPlace.score >= COMPANION_GO_LATER_SCORE) {
    return {
      action: "go-later",
      score: bestPlace.score,
      placeId: bestPlace.id,
      confidence,
      verdict: bestPlace.bestWindow
        ? `Go later: ${bestPlace.name} looks best around ${bestPlace.bestWindow}.`
        : `Go later: ${bestPlace.name} is useful, but not urgent.`,
      reason: `${scoreReason(bestPlace, freshness)} ${supportReason(input, bestPlace)}`,
    };
  }

  return {
    action: "do-not-bother",
    score: bestPlace.score,
    placeId: bestPlace.id,
    confidence,
    verdict: `Not today: ${bestPlace.name} looks weak right now.`,
    reason: `${scoreReason(bestPlace, freshness)} Check again when the next update lands.`,
  };
}

function missingScoreDecision(input: CompanionDecisionInput): CompanionDecision {
  if (input.latestCall) {
    return {
      action: "go-later",
      score: null,
      placeId: null,
      confidence: "low",
      verdict: "Go later: check The Call before changing plans.",
      reason: `${input.latestCall.title} is available, but there is no fresh saved-place score to confirm it here.`,
    };
  }

  if (input.latestBriefing) {
    return {
      action: "go-later",
      score: null,
      placeId: null,
      confidence: "low",
      verdict: "Go later: read the latest Briefing first.",
      reason: input.latestBriefing.placeName
        ? `${input.latestBriefing.title} is ready for ${input.latestBriefing.placeName}, but there is no fresh saved-place score to confirm it here.`
        : `${input.latestBriefing.title} is ready, but there is no fresh saved-place score to confirm it here.`,
    };
  }

  return {
    action: "do-not-bother",
    score: null,
    placeId: null,
    confidence: "low",
    verdict: "No saved signal yet.",
    reason: "Save a mountain or coast place to unlock a proper daily decision.",
  };
}

export function rankCompanionDecisionPlaces<T extends CompanionDecisionPlace>(
  places: T[],
  now: Date,
): T[] {
  return [...places].sort((a, b) => {
    const scoreDelta = decisionScore(b, now) - decisionScore(a, now);
    if (scoreDelta !== 0) return scoreDelta;
    const recencyDelta = timestampMs(b.updatedAt) - timestampMs(a.updatedAt);
    if (recencyDelta !== 0) return recencyDelta;
    return a.name.localeCompare(b.name, "en-GB");
  });
}

function decisionScore(place: CompanionDecisionPlace, now: Date): number {
  if (place.score === null) return -1;
  const freshness = scoreFreshness(place.updatedAt, now);
  if (freshness.state === "stale") return place.score - 5;
  if (freshness.state === "missing") return place.score - 3;
  return place.score;
}

function decisionConfidence(
  place: CompanionDecisionPlace,
  freshness: CompanionFreshness,
  input: CompanionDecisionInput,
): CompanionDecisionConfidence {
  if (!isUsableFreshness(freshness)) return "low";
  if (place.scoreKind === "sport-specific" || callMatchesPlace(input.latestCall, place)) {
    return "high";
  }
  return "medium";
}

function scoreFreshness(
  updatedAt: string | null,
  now: Date,
): CompanionFreshness {
  return buildCompanionFreshness(updatedAt, now);
}

function scoreReason(
  place: CompanionDecisionPlace,
  freshness: CompanionFreshness,
): string {
  const scoreText = formatScore(place.score);
  const safety = safetyReason(place);
  if (place.bestWindow) {
    return `${place.name} is ${scoreText}, with the best window around ${place.bestWindow}.${safety}`;
  }
  if (freshness.state === "stale") {
    return `${place.name} is ${scoreText}, but that signal is stale.${safety}`;
  }
  if (freshness.state === "missing") {
    return `${place.name} is ${scoreText}, but PeakWave has no update time for it.${safety}`;
  }
  return `${place.name} is ${scoreText} on the latest saved-place signal.${safety}`;
}

function safetyReason(place: CompanionDecisionPlace): string {
  const note = place.safetyNotes?.find((value) => value.trim().length > 0);
  return note ? ` Safety note: ${note}.` : "";
}

function isUsableFreshness(freshness: CompanionFreshness): boolean {
  return freshness.state === "updated-today" || freshness.state === "updated-yesterday";
}

function supportReason(
  input: CompanionDecisionInput,
  place: CompanionDecisionPlace,
): string {
  if (callMatchesPlace(input.latestCall, place)) {
    return "The Call backs this place, so check it before you move.";
  }
  if (input.latestCall) {
    return "Your latest Call is also worth checking.";
  }
  if (briefingMatchesPlace(input.latestBriefing, place)) {
    return "A matching Briefing is ready for the detail.";
  }
  if (input.latestBriefing) {
    return "A fresh Briefing is ready for extra context.";
  }
  if (place.scoreKind === "generic-fallback") {
    return "This is a generic conditions score, not a personalised Pulse yet.";
  }
  return "This score is built from the saved sport context for this place.";
}

function callMatchesPlace(
  call: CompanionDecisionCall,
  place: CompanionDecisionPlace,
): boolean {
  return namesMatch(call?.placeName, place.name);
}

function briefingMatchesPlace(
  briefing: CompanionDecisionBriefing,
  place: CompanionDecisionPlace,
): boolean {
  return namesMatch(briefing?.placeName, place.name);
}

function namesMatch(a: string | null | undefined, b: string): boolean {
  return Boolean(a && a.trim().toLowerCase() === b.trim().toLowerCase());
}

function formatScore(score: number | null): string {
  if (score === null) return "no score";
  return `${scoreOutOf100(score)}/100`;
}

function timestampMs(value: string | null): number {
  if (!value) return 0;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : 0;
}

function inferDecisionNow(input: CompanionDecisionInput): Date {
  const timestamps = [
    ...input.savedPlaces.map((place) => place.updatedAt),
    input.latestBriefing?.publishedAt ?? null,
    input.latestCall?.startsAt ?? null,
  ]
    .map((value) => (value ? Date.parse(value) : NaN))
    .filter(Number.isFinite);

  if (timestamps.length === 0) return new Date(0);
  return new Date(Math.max(...timestamps));
}
